import { Component } from '@theme/component';
import { debounce, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * Fetch the complete header section once and share the response with the
 * mobile drawer, localization selector, and cart drawer.
 * @param {string} sectionId
 * @returns {Promise<string>}
 */
function requestHeaderSection(sectionId) {
  const url = new URL(window.location.href);
  url.searchParams.set('section_id', sectionId);

  const globalWindow =
    /** @type {Window & { __cbnHeaderSectionRequests?: Map<string, Promise<string>> }} */ (window);
  const cache = (globalWindow.__cbnHeaderSectionRequests ||= new Map());
  const cacheKey = url.toString();
  const cached = cache.get(cacheKey);

  if (cached) return cached;

  const request = fetch(cacheKey, {
    credentials: 'same-origin',
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  })
    .then((response) => {
      if (!response.ok) throw new Error(`Header section request failed with status ${response.status}`);
      return response.text();
    })
    .catch((error) => {
      cache.delete(cacheKey);
      throw error;
    });

  cache.set(cacheKey, request);
  return request;
}

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {Promise<void> | undefined} */
  #hydrationPromise;

  /**
   * Populate the empty submenu containers from the complete server-rendered
   * header while keeping the visible top-level navigation stable.
   * @returns {Promise<void>}
   */
  #hydrateMenu() {
    if (!this.hasAttribute('data-lazy-content')) return Promise.resolve();
    if (this.#hydrationPromise) return this.#hydrationPromise;

    const sectionId = this.dataset.sectionId;
    if (!sectionId) return Promise.resolve();

    this.#hydrationPromise = requestHeaderSection(sectionId)
      .then((html) => {
        if (!this.isConnected || !this.hasAttribute('data-lazy-content')) return;

        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const sourceMenu = parsed.querySelector(
          `header-menu[data-section-id="${CSS.escape(sectionId)}"]:not([data-lazy-content])`
        );
        if (!(sourceMenu instanceof HTMLElement)) {
          throw new Error('Deferred desktop menu content was not found');
        }

        const sourceSubmenus = [...sourceMenu.querySelectorAll('[ref="submenu[]"]')];
        const targetSubmenus = [...this.querySelectorAll('[ref="submenu[]"]')];
        if (sourceSubmenus.length !== targetSubmenus.length) {
          throw new Error('Deferred desktop menu structure did not match');
        }

        targetSubmenus.forEach((target, index) => {
          const source = sourceSubmenus[index];
          if (!(target instanceof HTMLElement) || !(source instanceof HTMLElement)) return;

          const fragment = document.createDocumentFragment();
          for (const child of source.children) {
            if (child.tagName === 'SCRIPT') continue;
            fragment.append(document.importNode(child, true));
          }
          target.replaceChildren(fragment);
        });

        this.removeAttribute('data-lazy-content');
        this.updatedCallback();
        this.#cacheMaxOverflowMenuHeight();

        const activeSubmenu = findSubmenu(this.#state.activeItem);
        if (activeSubmenu) {
          this.#loadImages(activeSubmenu);
          requestAnimationFrame(() => {
            this.headerComponent?.style.setProperty('--submenu-height', `${activeSubmenu.offsetHeight}px`);
          });
        }
      })
      .catch((error) => {
        this.#hydrationPromise = undefined;
        console.warn('Crafty deferred desktop menu could not be loaded.', error);
      });

    return this.#hydrationPromise;
  }

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#deactivate());
    this.addEventListener('pointerleave', this.#clearEscapeHoverLock);
    this.addEventListener('keydown', this.#keydownListener);
    // on load, cache the max height of the submenu so you can use it in a translate
    this.#cacheMaxOverflowMenuHeight();

    window.addEventListener('resize', this.#resizeListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.removeEventListener('pointerleave', this.#clearEscapeHoverLock);
    this.removeEventListener('keydown', this.#keydownListener);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    this.#cacheMaxOverflowMenuHeight();
    setHeaderMenuStyle();
  }, 100);

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  #suppressNextFocusActivation = false;

  #escapeHoverLocked = false;

  #keydownListener = (event) => {
    if (event.key !== 'Escape') {
      if (this.#escapeHoverLocked && ['Tab', 'ArrowDown', 'Enter', ' '].includes(event.key)) {
        this.#clearEscapeHoverLock();
      }

      return;
    }

    if (!this.#state.activeItem) return;

    const activeItem = this.#state.activeItem;
    const returnFocusTarget = this.dataset.overflowExpanded === 'true' ? this.overflowTrigger : activeItem;

    event.preventDefault();
    event.stopPropagation();

    this.#suppressNextFocusActivation = true;
    this.#lockEscapeHover();
    this.#deactivate(activeItem, true);
    returnFocusTarget?.focus({ preventScroll: true });

    queueMicrotask(() => {
      this.#suppressNextFocusActivation = false;
    });
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  get overflowTrigger() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-overflow-menu-trigger]'));
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (event instanceof PointerEvent && this.#escapeHoverLocked) return;
    this.#hydrateMenu();
    if (event instanceof FocusEvent && this.#suppressNextFocusActivation) return;
    if (event instanceof FocusEvent) this.#clearEscapeHoverLock();

    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isOverflowActivation = Boolean(event.target.closest('[slot="more"]'));
    const isDefaultSlot = !isOverflowActivation && event.target.slot === '';

    this.dataset.overflowExpanded = isOverflowActivation.toString();
    if (this.overflowTrigger) this.overflowTrigger.ariaExpanded = isOverflowActivation.toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      this.#loadImages(submenu);

      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.headerComponent?.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    if (!item || item != this.#state.activeItem) return;
    if (!force && this.overflowHovered) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';
    if (this.overflowTrigger) this.overflowTrigger.ariaExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * Start loading submenu images only after the customer opens that submenu.
   * @param {HTMLElement} submenu
   */
  #loadImages(submenu) {
    const images = submenu.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  }

  /**
   * Caches the maximum height of all submenus for consistent animations
   * Stores the value in a CSS custom property for use in transitions
   */
  #cacheMaxOverflowMenuHeight() {
    const submenus = this.querySelectorAll('[ref="submenu[]"]');
    const maxHeight = Math.max(
      0,
      ...Array.from(submenus)
        .filter((submenu) => submenu instanceof HTMLElement)
        .map((submenu) => submenu.offsetHeight)
    );
    this.headerComponent?.style.setProperty('--submenu-max-height', `${maxHeight}px`);
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }

  #lockEscapeHover() {
    this.#escapeHoverLocked = true;
    this.dataset.escapeHoverLock = 'true';
  }

  #clearEscapeHoverLock = () => {
    if (!this.#escapeHoverLocked) return;

    this.#escapeHoverLocked = false;
    delete this.dataset.escapeHoverLock;
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const overflowTrigger = element.closest('[slot="more"]');
  if (overflowTrigger) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(overflowTrigger.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
